import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError, AxiosResponse } from "axios";
import { FieldValues, UseFormReset } from "react-hook-form";
import { FileAttachment } from "../components/model/file-attachement";
import {
  FileField,
  FileRequest,
  FileResponse,
} from "../components/model/file-submit";
import { FormSubmit } from "../components/model/form-submit";
import {
  SubmitResponse,
  SubmitStatus,
} from "../components/model/submit-response";
import { url } from "../components/model/url-endpoint";
import { logger } from "../utils/logger";
import { removeFileAttachment } from "./file-attachement-slice";
import store from "./store";

export const initialState: SubmitResponse = {
  success: false,
  messages: undefined,
  status: SubmitStatus.VALIDATING,
  fileProgress: 0,
  dataProgress: [] as number[],
  traceId: undefined,
  correlationId: undefined,
};

const formSlice = createSlice({
  name: "submit",
  initialState,
  reducers: {
    submit: (state, action: PayloadAction<SubmitResponse>) => {
      return {
        ...state,
        success: action.payload.success,
        messages: action.payload.messages,
        status: action.payload.status,
        fileProgress: action.payload.fileProgress,
        dataProgress: action.payload.dataProgress,
        traceId: action.payload.traceId,
        correlationId: action.payload.correlationId,
      };
    },
  },
});

export const submitForm = (
  queryParams: URLSearchParams,
  form: FormSubmit,
  reset: UseFormReset<FieldValues>
) => {
  return (dispatch: Dispatch<any>) => {
    const fileAttachments: FileAttachment[] = store.getState().files;
    if (fileAttachments && fileAttachments.length > 0) {
      submitFileAttachments(
        fileAttachments,
        queryParams,
        dispatch,
        form,
        reset
      );
    } else {
      callSubmit(dispatch, form, reset);
    }
  };
};

const submitFileAttachments = (
  fileAttachments: FileAttachment[],
  queryParams: URLSearchParams,
  dispatch: Dispatch<any>,
  form: FormSubmit,
  reset: UseFormReset<FieldValues>
) => {
  axios
    .post<FormSubmit, AxiosResponse<SubmitResponse>>(url + "validate", form)
    .then((response: AxiosResponse<SubmitResponse>) => {
      if (response.data.success) {
        statusChange(dispatch, SubmitStatus.GET_UPLOAD_URL);
        getUploadUrl(fileAttachments, queryParams, dispatch, form, reset);
      } else {
        callSubmit(dispatch, form, reset);
      }
    })
    .catch((error: AxiosError) => {
      statusChange(dispatch, SubmitStatus.FORM_SUBMISSION_FAILED);
      logger.error(error.message);
    });
};

const getUploadUrl = (
  fileAttachments: FileAttachment[],
  queryParams: URLSearchParams,
  dispatch: Dispatch<any>,
  form: FormSubmit,
  reset: UseFormReset<FieldValues>
) => {
  axios
    .post<FileRequest, AxiosResponse<FileResponse>>(url + "get_upload_url", {
      fields: fileAttachments.map<FileField>((file) => {
        return { id: file.id, file_name: file.name } as FileField;
      }),
      coi: queryParams.get("coi"),
      form: { uuid: queryParams.get("form") },
    } as FileRequest)
    .then((response: AxiosResponse<FileResponse>) => {
      form = { ...form, request_id: response.data.id };
      statusChange(dispatch, SubmitStatus.READ_LOCAL_FILES);
      uploadtoS3(response, dispatch, form, fileAttachments, reset);
    })
    .catch((error: AxiosError) => {
      logger.error(error.message);
      throw error;
    });
};

const uploadtoS3 = (
  response: AxiosResponse<FileResponse>,
  dispatch: Dispatch<any>,
  form: FormSubmit,
  fileAttachments: FileAttachment[],
  reset: UseFormReset<FieldValues>
): void => {
  const fileResponse: FileResponse = response.data;
  const getUploadPromises: Promise<AxiosResponse<ArrayBuffer, any>>[] =
    fileAttachments.map<Promise<AxiosResponse<ArrayBuffer, any>>>((file) =>
      axios.get<ArrayBuffer>(file.file, {
        responseType: "arraybuffer",
      })
    );
  Promise.all(getUploadPromises)
    .then((responses) => {
      statusChange(dispatch, SubmitStatus.UPLOAD_TO_S3);
      const putToS3Promises: Promise<AxiosResponse<Uint8Array, any>>[] =
        responses.map<Promise<AxiosResponse<Uint8Array, any>>>(
          (axiosResponse, index) => {
            return axios.put<Uint8Array>(
              fileResponse.urls[index],
              new Uint8Array(
                axiosResponse.data,
                0,
                axiosResponse.data.byteLength
              ),
              {
                onUploadProgress(progressEvent) {
                  dispatch(
                    formActions.submit({
                      ...store.getState().submit,
                      dataProgress: [
                        ...store
                          .getState()
                          .submit.dataProgress?.slice(0, index),
                        Math.round(
                          (progressEvent.loaded * 100) /
                            (progressEvent.total ?? 1) /
                            store.getState().files.length
                        ),
                        ...store
                          .getState()
                          .submit.dataProgress?.slice(index + 1),
                      ],
                    } as SubmitResponse)
                  );
                  progressEvent;
                },
              }
            );
          }
        );
      putToS3Promises.forEach((p) => {
        p.then(() => {
          fileProgressChange(
            dispatch,
            store.getState().submit.fileProgress,
            putToS3Promises.length
          );
        }).catch((error) => {
          logger.error(error.message);
        });
      });
      Promise.all(putToS3Promises)
        .then(() => {
          statusChange(dispatch, SubmitStatus.SUBMIT_FORM);
          callSubmit(dispatch, form, reset);
        })
        .catch((error) => {
          logger.error(error.message);
          statusChange(dispatch, SubmitStatus.UPLOADS_FAILED);
        })
        .finally(() => {
          dispatch(
            formActions.submit({
              ...store.getState().submit,
              dataProgress: [] as number[],
            } as SubmitResponse)
          );
          fileResponse.urls.forEach((url) => URL.revokeObjectURL(url));
          fileAttachments.forEach((fa) => {
            store.dispatch(removeFileAttachment(fa.id));
          });
        });
    })
    .catch((error) => {
      logger.error(error.message);
      statusChange(dispatch, SubmitStatus.UPLOADS_FAILED);
    })
    .finally(() => {
      fileResponse.urls.forEach((url) => URL.revokeObjectURL(url));
    });
};

const callSubmit = (
  dispatch: Dispatch<any>,
  form: FormSubmit,
  reset: UseFormReset<FieldValues>
) => {
  axios
    .put<FormSubmit, AxiosResponse<SubmitResponse>>(url + "submit", form)
    .then((response: AxiosResponse<SubmitResponse>) => {
      dispatch(
        formActions.submit({
          ...response.data,
          status: SubmitStatus.FORM_SUBMITTED,
        } as SubmitResponse)
      );
      if (response.data.success) reset();
    })
    .catch((error: AxiosError) => {
      logger.error(error);
      statusChange(dispatch, SubmitStatus.FORM_SUBMISSION_FAILED, error);
    });
};

const statusChange = (
  dispatch: Dispatch<any>,
  status: SubmitStatus,
  error: AxiosError<unknown, any> | undefined = undefined
) => {
  dispatch(
    formActions.submit({
      ...(error?.response?.data as SubmitResponse),
      status: SubmitStatus.FORM_SUBMITTED,
    } as SubmitResponse)
  );
};

const fileProgressChange = (
  dispatch: Dispatch<any>,
  fileProgress: number,
  promisesLength: number
) => {
  dispatch(
    formActions.submit({
      ...store.getState().submit,
      fileProgress: fileProgress + 1,
    } as SubmitResponse)
  );
};

export default formSlice;
export const formActions = formSlice.actions;
